import React, { useEffect, useState } from 'react'

import { CarouselOverflow, HeadingLarge } from '@hm-group/fabric-components'
import { FadeSwap, Label, Carousel } from '@pb/components'
import { getLabelFromStorage, useMediaQuery, Breakpoints } from '@pb/utils'
import cn from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import Slider from 'react-slick'

import { TRACKING_EVENTS } from 'App/constants'
import appActions from 'App/duck/actions'
import appSelectors from 'App/duck/selectors'
import optimizelyConstants from 'constants/optimizely'
import { ACCOUNT_TRACKING_EVENTS } from 'features/account/constants'
import getAccountTracking from 'features/account/helpers/getAccountTracking'
import useOptimizelyDecision, {
  useNorthstarABTestEnabled,
} from 'features/account/hooks/useOptimizelyDecision'
import useOptimizelyToggle from 'features/account/hooks/useOptimizelyToggle'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import { trackEventAdapter } from 'helpers/tracking'
import SecondaryAction from 'ui/buttons/SecondaryAction'
import ErrorMessage from 'ui/ErrorMessage'

import { getMyNonExpiredBonus } from '../../duck/normalize'
import { operationFetchRewardsStart, operationFetchRewards } from '../../duck/operations'
import selectors from '../../duck/selectors'
import MyRewardsLoader from '../MyRewardsLoader'
import RewardHeading from './components/RewardHeading'
import renderHeading from './helpers/renderHeading'
import renderRewards from './helpers/renderRewards'
import renderVouchers from './helpers/renderVouchers'
import styles from './MyRewardsList.scss'

const MyRewardsList: React.FC = () => {
  const rewards = useSelector(selectors.getMyNonExpiredRewards)
  const myOffers = useSelector(selectors.getMyOffers)
  const validOffers = getMyNonExpiredBonus(myOffers)
  const myVouchers = useSelector(selectors.getMyVouchers)
  const validVouchers = getMyNonExpiredBonus(myVouchers)
  const isRewardsLoading = useSelector(selectors.getIsRewardsLoading)
  const shouldShowListGrid = useSelector(appSelectors.getIsDesktop)
  const isSmallScreen = useSelector(appSelectors.getIsSmallScreen)
  const isRewardsLoadingFailed = useSelector(selectors.getIsRewardsLoadingFailed)
  const wasRewardsResponseEmpty = useSelector(selectors.wasRewardsResponseEmpty)
  const shouldFetchRewards = useSelector(selectors.shouldFetchRewards)
  const dispatch = useDispatch()
  const [shouldShowAllRewards, setShouldShowAllRewards] = useState(false)

  const isApiRedesignEnable = useOptimizelyToggle({
    key: optimizelyConstants.ACCOUNT_CONFIGURED_OFFER_API_REDESIGN,
  })
  const isNewVouchersOffersEnabled = useOptimizelyToggle({
    key: optimizelyConstants.ACCOUNT_NEW_VOUCHERS_OFFERS,
  })

  const isNorthstarBrand = useOptimizelyDecision(optimizelyConstants.ACCOUNT_NORTHSTAR_VARIATION)
  const isNorthStarABtestEnabled = useNorthstarABTestEnabled(
    optimizelyConstants.ACCOUNT_NORTHSTAR_VARIATION,
  )

  const shouldShowViewAllButton = useMediaQuery(Breakpoints.MEDIUM)

  const appState = useSelector((state) => state)
  const { trackEvent } = getAccountTracking(appState)
  const isLessThanOrTwoRewards = rewards.length <= 2
  const initialAmountOfRewardsToShow = 6
  const shouldShowToggleButton = rewards.length > initialAmountOfRewardsToShow
  const renderUntilIndex = shouldShowAllRewards ? rewards.length : initialAmountOfRewardsToShow
  const showMoreLabel = shouldShowAllRewards ? 'member.button.showless' : 'member.button.showall'
  const labels = { next: 'next', prev: 'previous' }
  const numOfItemsPerSlide = {
    xsmall: 1.2,
    small: 1.2,
    medium: 1.2,
    large: 2,
    xlarge: 2.2,
    xxl: 2.2,
  }
  type NumOfSlides = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  const slidesPerBreakpoint: {
    default: NumOfSlides
    xsmall: NumOfSlides
    small: NumOfSlides
    medium: NumOfSlides
    large: NumOfSlides
    xlarge: NumOfSlides
    xxl: NumOfSlides
  } = {
    default: 3,
    xsmall: 2,
    small: 2,
    medium: 4,
    large: 4,
    xlarge: 7,
    xxl: 7,
  }

  const numOfItemsPerSlideOffers = { xsmall: 2, small: 2, medium: 2, large: 3, xlarge: 4, xxl: 4 }

  const trackRewardClick = trackEvent(ACCOUNT_TRACKING_EVENTS.viewReward.id)

  const setOfferTracking = (keyValue, typeValue): void => {
    dispatch(
      appActions.trackEvent({
        id: TRACKING_EVENTS.offersPropositionValue.id,
        offer_type: typeValue,
        offer_id: keyValue,
      }),
    )
  }

  useEffect(() => {
    if (shouldFetchRewards) {
      if (isApiRedesignEnable) {
        dispatch(operationFetchRewards(setOfferTracking, isNewVouchersOffersEnabled))
      } else {
        dispatch(operationFetchRewardsStart(setOfferTracking))
      }
    }
  }, [shouldFetchRewards])

  const handleVouchersClick = (offerKey, offerPropositionId): void => {
    trackEventAdapter({
      ...ACCOUNT_TRACKING_EVENTS.viewVouchersFromCarousel,
      event_label: 'offerid',
    })
    trackEventAdapter({
      ...ACCOUNT_TRACKING_EVENTS.clickRewardCarouselToVoucher,
      event_label: `${offerKey}_${offerPropositionId} from carousel`,
    })
    trackRewardClick()
  }

  const trackViewAllCTA = (): void => {
    if (isNorthStarABtestEnabled) {
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_VIEW_ALL_BUTTON)
    }
  }

  if (isRewardsLoadingFailed) {
    return <ErrorMessage title={getLabelFromStorage('hub.custumer.rewards.los.error')} />
  }

  if (wasRewardsResponseEmpty) {
    return null
  }

  return (
    <FadeSwap flag={isRewardsLoading} placeholder={<MyRewardsLoader />}>
      <div className={styles.container}>
        {isNewVouchersOffersEnabled ? (
          <>
            {shouldShowViewAllButton
              ? renderHeading(
                  isNorthstarBrand
                    ? getLabelFromStorage('account.rewards.vouchers.ns')
                    : getLabelFromStorage('account.rewards.vouchers'),
                  isNorthstarBrand,
                  trackViewAllCTA(),
                )
              : isNorthstarBrand && (
                  <HeadingLarge className={styles.viewAllHeading}>
                    {getLabelFromStorage('account.rewards.vouchers.ns')}
                  </HeadingLarge>
                )}
            {isNorthstarBrand ? (
              <CarouselOverflow
                labels={labels}
                slidesPerBreakpoint={slidesPerBreakpoint}
                slideSpacing="xsmall"
              >
                {renderRewards(
                  validVouchers,
                  validVouchers.length,
                  isNewVouchersOffersEnabled,
                  true,
                  styles.offers,
                )}
              </CarouselOverflow>
            ) : (
              <Carousel
                labels={labels}
                numOfItemsPerSlide={numOfItemsPerSlide}
                isInfinite={!shouldShowViewAllButton}
                onlyArrows={!shouldShowViewAllButton}
                {...(!shouldShowViewAllButton && {
                  title: getLabelFromStorage('account.rewards.vouchers'),
                })}
                isTitleCenterAligned
                shouldRenderEmptySlides={false}
              >
                {renderVouchers(validVouchers, handleVouchersClick)}
              </Carousel>
            )}
            {validVouchers?.length === 0 && shouldShowViewAllButton && (
              <div className={styles.noRewards}>
                {getLabelFromStorage('checkout.onlinevoucher.novoucheravailable')}
              </div>
            )}
            {shouldShowViewAllButton
              ? renderHeading(
                  isNorthstarBrand
                    ? getLabelFromStorage('account.rewards.offers')
                    : getLabelFromStorage('account.rewards.offers.ns'),
                  isNorthstarBrand,
                  trackViewAllCTA(),
                )
              : isNorthstarBrand && (
                  <HeadingLarge className={styles.viewAllHeading}>
                    {getLabelFromStorage('account.rewards.offers.ns')}
                  </HeadingLarge>
                )}
            {isNorthstarBrand ? (
              <CarouselOverflow
                labels={labels}
                slidesPerBreakpoint={slidesPerBreakpoint}
                slideSpacing="xsmall"
              >
                {renderRewards(
                  validOffers,
                  validOffers.length,
                  isNewVouchersOffersEnabled,
                  true,
                  styles.offers,
                )}
              </CarouselOverflow>
            ) : (
              <Carousel
                labels={labels}
                numOfItemsPerSlide={numOfItemsPerSlideOffers}
                isInfinite={!shouldShowViewAllButton}
                onlyArrows={!shouldShowViewAllButton}
                {...(!shouldShowViewAllButton && {
                  title: getLabelFromStorage('account.rewards.offers'),
                })}
                isTitleCenterAligned
              >
                {renderRewards(
                  validOffers,
                  validOffers.length,
                  isNewVouchersOffersEnabled,
                  false,
                  styles.offers,
                  'li',
                )}
              </Carousel>
            )}
            {validOffers?.length === 0 && shouldShowViewAllButton && (
              <div className={styles.noRewards}>
                {getLabelFromStorage('checkout.onlinevoucher.novoucheravailable')}
              </div>
            )}
          </>
        ) : (
          <>
            <RewardHeading totalRewardItems={rewards.length} />
            {shouldShowListGrid ? (
              <>
                <ul
                  className={cn(
                    styles.listGrid,
                    isLessThanOrTwoRewards && styles.lessThanTwoRewards,
                  )}
                >
                  {renderRewards(rewards, renderUntilIndex, isNewVouchersOffersEnabled)}
                </ul>
                {shouldShowToggleButton && (
                  <SecondaryAction
                    onClick={() => setShouldShowAllRewards(!shouldShowAllRewards)}
                    className={styles.showMoreButton}
                  >
                    <Label copyKey={showMoreLabel} />
                  </SecondaryAction>
                )}
              </>
            ) : (
              <Slider
                className={styles.carousel}
                dotsClass={styles.dots}
                slidesToShow={isSmallScreen || rewards.length < 2 ? 1 : 2}
                dots
                arrows={false}
                accessibility
                infinite
                centerMode
              >
                {renderRewards(
                  rewards,
                  rewards.length,
                  isNewVouchersOffersEnabled,
                  false,
                  '',
                  'div',
                )}
              </Slider>
            )}
          </>
        )}
      </div>
    </FadeSwap>
  )
}

export default MyRewardsList
